import * as vp from 'vopay';
import { FunctionComponent, useContext, useMemo } from 'react';
import { DialogueHandler, WorkflowDialogueHandler } from './types';
import WorkflowDialogueContext from './WorkflowContext';
import { useTranslation } from 'react-i18next';

const { ConfirmationDialogue, Box } = vp;

type WorkflowDialogueProps = {
  handleDialogue: DialogueHandler<WorkflowDialogueHandler>;
};

type FC = FunctionComponent<WorkflowDialogueProps>;

const WorkflowDialogue: FC = ({ handleDialogue }: WorkflowDialogueProps) => {
  const {
    showWorkflowActivate,
    showWorkflowDeactivate,
    showWorkflowErrorAlert,
    workflowError,
    workflowName,
    showWorkflowSave,
    showWorkflowCreate,
    workflowDescription,
  } = useContext(WorkflowDialogueContext);
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows.Dialogue' });

  return useMemo(
    () => (
      <div>
        {/* Workflow Error */}
        <ConfirmationDialogue
          isVisible={showWorkflowErrorAlert}
          title={t('NotUpdatedError.title')}
          status="error"
          subTitle={`${workflowError}`}
          confirm={{
            label: t('NotUpdatedError.back'),
            action: () => handleDialogue({ type: 'error', state: 'confirm' }),
          }}
        />
        {/* Workflow Deactivate */}
        <ConfirmationDialogue
          isVisible={showWorkflowDeactivate}
          title={t('DeactivateConfirmation.title')}
          subTitle={
            <Box flx="col">
              <Box pb="sm">{t('DeactivateConfirmation.subTitleFirst')}</Box>
              <Box>{t('DeactivateConfirmation.subTitleSecond')}</Box>
            </Box>
          }
          status="alert"
          confirm={{
            label: t('DeactivateConfirmation.confirmButton'),
            action: () => handleDialogue({ type: 'deactivate', state: 'confirm' }),
          }}
          cancel={{
            label: t('DeactivateConfirmation.cancelButton'),
            action: () => handleDialogue({ type: 'deactivate', state: 'cancel' }),
          }}
          close={{
            action: () => handleDialogue({ type: 'deactivate', state: 'close' }),
          }}
        />
        {/* Workflow Activate */}
        <ConfirmationDialogue
          isVisible={showWorkflowActivate}
          title={t('ActivateConfirmation.title')}
          subTitle={
            <Box flx="col">
              <Box pb="sm">{t('ActivateConfirmation.subTitleFirst')}</Box>
              <Box>{t('ActivateConfirmation.subTitleSecond')}</Box>
            </Box>
          }
          status="alert"
          confirm={{
            label: t('ActivateConfirmation.confirmButton'),
            action: () => handleDialogue({ type: 'activate', state: 'confirm' }),
          }}
          cancel={{
            label: t('ActivateConfirmation.cancelButton'),
            action: () => handleDialogue({ type: 'activate', state: 'cancel' }),
          }}
          close={{
            action: () => handleDialogue({ type: 'activate', state: 'close' }),
          }}
        />
        {/* Workflow Create */}
        <ConfirmationDialogue
          isVisible={showWorkflowCreate}
          title={t('CreateConfirmation.title')}
          subTitle={
            <Box flx="col" gap="sm">
              {workflowDescription !== '' && (
                <Box>
                  <span style={{ fontWeight: 'bold' }}>{workflowDescription}</span>
                </Box>
              )}
              <Box jc="center">{t('CreateConfirmation.title')}</Box>
            </Box>
          }
          status="alert"
          confirm={{
            label: t('CreateConfirmation.confirmButton'),
            action: () => handleDialogue({ type: 'create', state: 'confirm' }),
          }}
          cancel={{
            label: t('CreateConfirmation.cancelButton'),
            action: () => handleDialogue({ type: 'create', state: 'cancel' }),
          }}
          close={{
            action: () => handleDialogue({ type: 'create', state: 'close' }),
          }}
        />
        {/* Workflow Save */}
        <ConfirmationDialogue
          isVisible={showWorkflowSave}
          title={t('SaveChangesConfirmation.title')}
          subTitle={
            <Box flx="col" gap="sm">
              {workflowDescription !== '' && (
                <Box>
                  <span style={{ fontWeight: 'bold' }}>{workflowDescription}</span>
                </Box>
              )}
              <Box jc="center">{t('SaveChangesConfirmation.subTitle')}</Box>
            </Box>
          }
          status="alert"
          confirm={{
            label: t('SaveChangesConfirmation.confirmButton'),
            action: () => handleDialogue({ type: 'save', state: 'confirm' }),
          }}
          cancel={{
            label: t('SaveChangesConfirmation.cancelButton'),
            action: () => handleDialogue({ type: 'save', state: 'cancel' }),
          }}
          close={{
            action: () => handleDialogue({ type: 'save', state: 'close' }),
          }}
        />
      </div>
    ),
    [
      showWorkflowActivate,
      showWorkflowDeactivate,
      showWorkflowErrorAlert,
      workflowError,
      workflowName,
      showWorkflowSave,
      showWorkflowCreate,
      workflowDescription,
    ],
  );
};

export default WorkflowDialogue;
